import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Pencil, UserX, Trash2, ShieldCheck, Send, Search, ChevronUp, ChevronDown, ChevronsUpDown, X } from "lucide-react";
import { adminApi } from "../../api";
import { useAuth } from "../../hooks/useAuth";
import { ROLES } from "../../constants/roles";
import { roleBadgeClass } from "../../utils";
import DeleteUserModal from "./DeleteUserModal";
import { Button } from "../ui";
import type { AppUser } from "../../types/user";

const PAGE_SIZE = 20;

type SortKey = "full_name" | "email" | "role" | "team" | "is_active" | "last_login" | "last_active" | "created_at";
type SortDir = "asc" | "desc";

interface UserTableProps {
  users: AppUser[];
  onRefresh: () => void;
}

function SortIcon({ col, sortKey, sortDir }: { col: SortKey; sortKey: SortKey | null; sortDir: SortDir }) {
  if (sortKey !== col) return <ChevronsUpDown size={13} className="ml-1 shrink-0 opacity-30" />;
  return sortDir === "asc"
    ? <ChevronUp size={13} className="ml-1 shrink-0 text-blue-500" />
    : <ChevronDown size={13} className="ml-1 shrink-0 text-blue-500" />;
}

function SortableTh({ col, label, sortKey, sortDir, onSort }: {
  col: SortKey; label: string; sortKey: SortKey | null; sortDir: SortDir; onSort: (c: SortKey) => void;
}) {
  return (
    <th
      className="cursor-pointer select-none px-4 py-3 text-left"
      onClick={() => onSort(col)}
    >
      <span className="inline-flex items-center">
        {label}
        <SortIcon col={col} sortKey={sortKey} sortDir={sortDir} />
      </span>
    </th>
  );
}

function getSortValue(u: AppUser, key: SortKey): string | number {
  switch (key) {
    case "full_name":   return (u.full_name ?? "").toLowerCase();
    case "email":       return (u.email ?? "").toLowerCase();
    case "role":        return (u.role?.name ?? "").toLowerCase();
    case "team":        return (u.team?.name ?? "").toLowerCase();
    case "is_active":   return u.is_active ? 0 : 1;
    case "last_login":  return u.last_login  ? new Date(u.last_login).getTime()  : 0;
    case "last_active": return u.last_active ? new Date(u.last_active).getTime() : 0;
    case "created_at":  return u.created_at  ? new Date(u.created_at).getTime()  : 0;
    default: return "";
  }
}

export default function UserTable({ users, onRefresh }: UserTableProps) {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = currentUser?.role?.name === ROLES.SUPER_ADMIN;

  const [deleteTarget, setDeleteTarget] = useState<AppUser | null>(null);
  const [resendingInvite, setResendingInvite] = useState<string | null>(null);
  const [resentIds, setResentIds] = useState<Set<string>>(new Set());

  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey | null>("created_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(1);

  function handleSort(col: SortKey) {
    if (sortKey === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(col);
      setSortDir("asc");
    }
    setPage(1);
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q
      ? users.filter(
          (u) =>
            (u.full_name ?? "").toLowerCase().includes(q) ||
            (u.email ?? "").toLowerCase().includes(q),
        )
      : users;
  }, [users, search]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const av = getSortValue(a, sortKey);
      const bv = getSortValue(b, sortKey);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  function handleSearchChange(val: string) {
    setSearch(val);
    setPage(1);
  }

  async function deactivate(id: string) {
    if (!confirm("Deactivate this user?")) return;
    await adminApi.deactivateUser(id);
    onRefresh();
  }

  async function resendInvite(id: string) {
    setResendingInvite(id);
    try {
      await adminApi.resendInvite(id);
      setResentIds((prev) => new Set(prev).add(id));
    } finally {
      setResendingInvite(null);
    }
  }

  function formatDate(val?: string | null) {
    if (!val) return <span className="text-gray-300">—</span>;
    return new Date(val).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  }

  function formatDateTime(val?: string | null) {
    if (!val) return <span className="text-gray-300">Never</span>;
    return new Date(val).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  }

  const sortProps = { sortKey, sortDir, onSort: handleSort };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-4">
        <h2 className="text-base font-semibold text-gray-800">
          Users
          {search && filtered.length !== users.length && (
            <span className="ml-2 text-sm font-normal text-gray-400">
              {filtered.length} of {users.length}
            </span>
          )}
        </h2>
        <Button onClick={() => navigate("/admin/users/invite")}>
          <UserPlus size={15} />
          Invite user
        </Button>
      </div>

      {/* Search */}
      <div className="mb-3 relative max-w-sm">
        <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-8 pr-8 text-sm text-gray-700 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
        />
        {search && (
          <button
            onClick={() => handleSearchChange("")}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={13} />
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              <SortableTh col="full_name"   label="Name"       {...sortProps} />
              <SortableTh col="email"       label="Email"      {...sortProps} />
              <SortableTh col="role"        label="Role"       {...sortProps} />
              <SortableTh col="team"        label="Team"       {...sortProps} />
              <SortableTh col="is_active"   label="Status"     {...sortProps} />
              <th className="px-4 py-3 text-left">2FA</th>
              <SortableTh col="last_login"  label="Last login"  {...sortProps} />
              <SortableTh col="last_active" label="Last active" {...sortProps} />
              <SortableTh col="created_at"  label="Created"     {...sortProps} />
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center text-sm text-gray-400">
                  {search ? `No users match "${search}"` : "No users found."}
                </td>
              </tr>
            ) : (
              pageRows.map((u) => (
                <tr key={u.id} className={`hover:bg-gray-50 ${!u.is_active ? "opacity-60" : ""}`}>
                  <td className="px-4 py-3 font-medium text-gray-800">{u.full_name}</td>
                  <td className="px-4 py-3 text-gray-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${roleBadgeClass(u.role?.name)}`}>
                      {u.role?.name ?? "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {u.team
                      ? <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">{u.team.name}</span>
                      : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${u.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                      {u.is_active ? "Active" : "Deactivated"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {u.totp_enabled
                      ? <span title="2FA enabled"><ShieldCheck size={15} className="text-green-600" /></span>
                      : <span className="text-gray-300 text-xs">Off</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{formatDateTime(u.last_login)}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{formatDateTime(u.last_active)}</td>
                  <td className="px-4 py-3 text-gray-500">{formatDate(u.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {u.is_active && !u.last_login && (
                        <button
                          onClick={() => resendInvite(u.id)}
                          disabled={resendingInvite === u.id}
                          className="rounded p-1 text-gray-400 hover:text-indigo-600 disabled:opacity-40"
                          title={resentIds.has(u.id) ? "Invite resent" : "Resend invite"}
                        >
                          <Send size={14} className={resentIds.has(u.id) ? "text-indigo-500" : ""} />
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/admin/users/${u.id}`)}
                        className="rounded p-1 text-gray-400 hover:text-blue-600"
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                      {isSuperAdmin && u.id !== currentUser?.id && u.is_active && (
                        <button
                          onClick={() => deactivate(u.id)}
                          className="rounded p-1 text-gray-400 hover:text-orange-500"
                          title="Deactivate"
                        >
                          <UserX size={14} />
                        </button>
                      )}
                      {isSuperAdmin && !u.is_active && (
                        <button
                          onClick={() => setDeleteTarget(u)}
                          className="rounded p-1 text-gray-400 hover:text-red-600"
                          title="Delete permanently"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
          <span>
            Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, sorted.length)} of {sorted.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs hover:border-gray-300 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="px-2 text-xs">Page {safePage} of {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs hover:border-gray-300 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {deleteTarget && (
        <DeleteUserModal user={deleteTarget} onClose={() => setDeleteTarget(null)} onSuccess={() => { setDeleteTarget(null); onRefresh(); }} />
      )}
    </div>
  );
}
